import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { env, type Region, type ServiceMeta, useKeywordMetadata } from '@kc-console/context';
import { StatusServiceKeyword } from '@kc-console/guard';

import type { UseQueryOptions } from '@tanstack/react-query';
import type { RegionEndpointVariableValue } from '@/shared/lib/keywordVariableParsing';

import {
  isRegionEndpointVariableKey,
  parseRegionEndpointVariableValue,
} from '@/shared/lib/keywordVariableParsing';

export type UseRegionEndpointOptions = UseQueryOptions<ServiceMeta>;

export type { RegionEndpointVariableValue };

export type ParsedRegionEndpointVariables = Record<string, RegionEndpointVariableValue>;

export type UseRegionEndpointResult = {
  currentRegion: string;
  currentRegionEndpointInfo: RegionEndpointVariableValue;
  regionList: string[];
};

const DEFAULT_ENDPOINT_INFO: RegionEndpointVariableValue = {
  internal: env.KC_FALLBACK_STATUS_INTERNAL_ENDPOINT || '',
  public: env.KC_FALLBACK_STATUS_ENDPOINT || '',
};

const DEFAULT_REGION = env.KC_DEFAULT_STATUS_REGION || '';

const emptyRegionState = () => ({
  parsedVariables: { [DEFAULT_REGION]: DEFAULT_ENDPOINT_INFO } as ParsedRegionEndpointVariables,
  variableKeys: [DEFAULT_REGION],
});

const buildParsedRegionVariables = (variables: Record<string, string> | undefined) => {
  if (!variables) {
    return emptyRegionState();
  }

  const variableKeys = Object.keys(variables).filter(isRegionEndpointVariableKey);
  if (variableKeys.length === 0) {
    return emptyRegionState();
  }

  const parsedVariables: ParsedRegionEndpointVariables = {};
  for (const key of variableKeys) {
    const value = parseRegionEndpointVariableValue(variables[key]);
    if (value) {
      parsedVariables[key] = value;
    }
  }

  return { parsedVariables, variableKeys };
};

/**
 * 현재 리전, 현재 리전 엔드포인트, 전체 리전 리스트 정보를 반환하는 Hook
 * @param region - `useKeywordMetadata`에 넘기는 리전(미지정 시 훅 내부에서 처리)
 * @param options - keyword 메타데이터 쿼리 옵션
 */
const useRegionEndpoint = (region?: Region, options?: UseRegionEndpointOptions) => {
  const router = useRouter();
  const { region: regionQuery } = router.query;
  const { data: keywordMetadata } = useKeywordMetadata(StatusServiceKeyword, region, options);

  const { parsedVariables, variableKeys } = useMemo(
    () => buildParsedRegionVariables(keywordMetadata?.variables),
    [keywordMetadata?.variables],
  );

  const currentRegion = useMemo(() => {
    const queryRegion = regionQuery as string | undefined;
    return queryRegion && variableKeys.includes(queryRegion) ? queryRegion : variableKeys[0];
  }, [regionQuery, variableKeys]);

  const currentRegionEndpointInfo = useMemo(
    () => parsedVariables[currentRegion] ?? DEFAULT_ENDPOINT_INFO,
    [currentRegion, parsedVariables],
  );

  return {
    currentRegionEndpointInfo,
    regionList: variableKeys,
    currentRegion,
  };
};

export default useRegionEndpoint;
